import type { Paginated } from '@app-types/index';

// ---------------------------------------------------------------------------
// Wire-format list envelope (snake_case), as DRF's pagination emits it.
// Kept private to the api/ layer — callers only see Paginated<DomainType>.
// ---------------------------------------------------------------------------
export interface RawPaginated<T> {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Maps a raw paginated envelope into the camelCase Paginated<T>, running each
// raw item through `mapItem`. This is THE helper every list endpoint reuses.
export const mapPaginated = <Raw, T>(
  raw: RawPaginated<Raw>,
  mapItem: (item: Raw) => T,
): Paginated<T> => ({
  count: raw.count,
  page: raw.page,
  pageSize: raw.page_size,
  totalPages: raw.total_pages,
  next: raw.next,
  previous: raw.previous,
  results: raw.results.map(mapItem),
});

// Build a query-string params object, dropping undefined/null/'' values so we
// never send empty filters. Returned shape is axios `{ params }`-compatible.
export const buildParams = (
  input: Record<string, string | number | boolean | undefined | null>,
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
};
